"use client";

import {
  AnimatePresence,
  motion,
  useMotionTemplate,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";

// Idle anchor for the spotlight when the cursor isn't over the panel.
// Matches the watermark's lower-right position so the NF stays backlit
// when nothing's being hovered.
const SPOTLIGHT_IDLE_X = 85;
const SPOTLIGHT_IDLE_Y = 80;

type AuthPanelVariant = {
  headlineLine1: string;
  headlineLine2: string;
  subhead: string;
};

type AuthPanelCopy = {
  tagline: string;
  eyebrow: string;
  variants: AuthPanelVariant[];
  showSignOut: boolean;
  // The small-viewport wordmark this shell adds on the cream side. Routes
  // that render a wordmark of their own opt out rather than stack two.
  showWordmark: boolean;
};

// Cycled copy variants on the login/discovery screen. Kept punchy and free of
// em dashes per design.
const LOGIN_VARIANTS: AuthPanelVariant[] = [
  {
    headlineLine1: "The engine",
    headlineLine2: "kept working.",
    subhead: "Pick up where you left off. Your batches are running.",
  },
  {
    headlineLine1: "The data",
    headlineLine2: "called it.",
    subhead: "Winners flagged on p value, not on a hunch.",
  },
];

const SELECT_ORG_COPY: AuthPanelCopy = {
  tagline: "Choose workspace · Step 2 of 2",
  eyebrow: "— Signed in",
  showSignOut: true,
  showWordmark: true,
  variants: [
    {
      headlineLine1: "Pick a",
      headlineLine2: "workspace.",
      subhead:
        "All your client accounts in one place. Switch any time from the avatar menu.",
    },
  ],
};

const DEFAULT_COPY: AuthPanelCopy = {
  tagline: "The Creative Intelligence Co.",
  eyebrow: "— Welcome back",
  showSignOut: false,
  showWordmark: true,
  variants: LOGIN_VARIANTS,
};

function getAuthPanelCopy(pathname: string | null): AuthPanelCopy {
  if (pathname?.startsWith("/select-organization")) return SELECT_ORG_COPY;
  // /smsmfa opens with its own 4rem wordmark; the shell's would stack a
  // second brand mark directly above it on small viewports.
  if (pathname?.startsWith("/smsmfa"))
    return { ...DEFAULT_COPY, showWordmark: false };
  return DEFAULT_COPY;
}

const VARIANT_CYCLE_MS = 8_000;

async function handleSignOut() {
  try {
    await fetch("/api/logout", { method: "POST" });
  } catch {
    // Best-effort — fall through to the redirect even if the request fails so
    // the user isn't stuck on a screen they're trying to leave.
  }
  window.location.href = "/discovery";
}

export default function AuthLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const pathname = usePathname();
  const copy = getAuthPanelCopy(pathname);
  const variantCount = copy.variants.length;
  const [variantIndex, setVariantIndex] = useState(0);

  // Reset to the first variant whenever the active copy set changes (e.g.
  // navigating between login → select-organization). AuthLayout doesn't
  // remount across these routes — same shell — so without depending on
  // pathname the counter would carry over.
  useEffect(() => {
    setVariantIndex(0);
  }, [pathname]);

  useEffect(() => {
    if (variantCount <= 1) return;
    const interval = setInterval(() => {
      setVariantIndex((i) => (i + 1) % variantCount);
    }, VARIANT_CYCLE_MS);
    return () => clearInterval(interval);
  }, [variantCount]);

  const variant = copy.variants[variantIndex] ?? copy.variants[0];

  // Spotlight that follows the cursor across the dark panel. The raw motion
  // values jump straight to wherever the cursor is; the springs trail behind
  // them, so the light reads as having weight and never snaps.
  const rawSpotX = useMotionValue(SPOTLIGHT_IDLE_X);
  const rawSpotY = useMotionValue(SPOTLIGHT_IDLE_Y);
  // Stiffness/damping tuned for a soft trail (not a slingshot, not molasses).
  // ~150ms to settle after a small move; longer for big sweeps. mass keeps
  // the easing feel symmetric across distances.
  const spotX = useSpring(rawSpotX, { stiffness: 90, damping: 22, mass: 0.6 });
  const spotY = useSpring(rawSpotY, { stiffness: 90, damping: 22, mass: 0.6 });

  const handleAsideMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    rawSpotX.set(((e.clientX - rect.left) / rect.width) * 100);
    rawSpotY.set(((e.clientY - rect.top) / rect.height) * 100);
  };
  const handleAsideMouseLeave = () => {
    // Drift back to the watermark anchor when the user moves away — keeps
    // the NF backlit while the panel is idle.
    rawSpotX.set(SPOTLIGHT_IDLE_X);
    rawSpotY.set(SPOTLIGHT_IDLE_Y);
  };
  const spotlightBackground = useMotionTemplate`radial-gradient(circle at ${spotX}% ${spotY}%, rgba(143, 28, 244, 0.14), transparent 60%)`;

  return (
    <div className="flex flex-row w-full min-h-screen bg-nf-ds-cream">
      <aside
        onMouseMove={handleAsideMouseMove}
        onMouseLeave={handleAsideMouseLeave}
        className="hidden lg:block lg:fixed lg:left-0 lg:w-1/2 h-screen bg-nf-ds-black text-nf-ds-white overflow-hidden"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.023] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
            backgroundSize: "140px 140px",
          }}
        />

        {/* Cursor-tracked spotlight — driven by spring-smoothed motion
            values bound through useMotionTemplate, so the radial gradient
            re-centers on every spring tick without React re-rendering. */}
        <motion.div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{ background: spotlightBackground }}
        />

        {/* Wordmark watermark — cream NF in the brand serif, overflowing
            the bottom-right edge of the dark panel. A diagonal gradient
            inside the letterforms washes from cream on the left into the
            product purple on the right for the "lighting" effect.
            Hidden below lg because the panel itself is hidden there. */}
        <div
          aria-hidden="true"
          className="hidden lg:block absolute right-0 bottom-0 pointer-events-none overflow-hidden select-none"
          style={{ height: "75%", width: "75%" }}
        >
          <span
            className="absolute font-swear leading-none tracking-[-0.02em]"
            style={{
              right: "-4%",
              bottom: "-4%",
              fontSize: "clamp(200px, 30vh, 420px)",
              backgroundImage:
                "linear-gradient(110deg, rgba(255, 255, 255, 0.025) 28%, rgba(143, 28, 244, 0.12) 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
              WebkitTextFillColor: "transparent",
            }}
          >
            NF
          </span>
        </div>

        <div className="absolute top-nf-ds-10 left-nf-ds-10 xl:top-nf-ds-12 xl:left-nf-ds-16 flex items-center gap-nf-ds-4">
          <span className="font-swear text-nf-ds-2xl leading-none tracking-[-0.01em]">
            NewForm
          </span>
          <span className="h-5 w-px bg-white/30" />
          {/* leading-none collapses the tagline's line-box to its glyph
              height; without it items-center centers a 24px-tall wordmark
              against a ~16px-tall (1.5 line-height) tagline box and the
              visual midlines don't match. */}
          <span className="font-nf-ds-mono text-[10.5px] uppercase tracking-[0.12em] leading-none text-nf-ds-white/70">
            {copy.tagline}
          </span>
        </div>

        <div className="absolute inset-0 flex items-center px-nf-ds-10 xl:px-nf-ds-16">
          {/* Explicit width (not just max-width) — the cycling content uses
              absolute positioning so it contributes no intrinsic width to the
              column. Without an explicit width the column would collapse to
              the eyebrow's natural width and wrap the headline word-by-word. */}
          <div
            className="flex flex-col gap-nf-ds-6"
            style={{ width: "clamp(320px, 32vw, 560px)" }}
          >
            <span className="font-nf-ds-mono text-[10.5px] uppercase tracking-[0.18em] text-nf-ds-white/70">
              {copy.eyebrow}
            </span>
            {/* Reserve vertical space so cross-blur swaps don't collapse the
                surrounding layout. Tuned for the tallest variant (2 headline
                lines + 2 subhead lines) at the max font size. */}
            <div
              className="relative w-full"
              style={{ minHeight: "clamp(180px, 22vw, 280px)" }}
            >
              <AnimatePresence mode="sync">
                <motion.div
                  key={variantIndex}
                  initial={{ opacity: 0, filter: "blur(16px)" }}
                  animate={{ opacity: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, filter: "blur(16px)" }}
                  transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
                  className="absolute inset-0 flex flex-col gap-nf-ds-6"
                  style={{ willChange: "filter, opacity" }}
                >
                  <h1
                    className="font-nf-ds-serif font-[300] leading-[0.95] tracking-[-0.025em]"
                    style={{ fontSize: "clamp(40px, 4.6vw, 72px)" }}
                  >
                    {variant.headlineLine1}
                    <br />
                    <em className="italic font-[300] text-nf-ds-purple-product">
                      {variant.headlineLine2}
                    </em>
                  </h1>
                  <p className="font-nf-ds-sans text-nf-ds-md leading-[1.5] text-nf-ds-white/70 max-w-[380px]">
                    {variant.subhead}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>

        <span className="absolute bottom-nf-ds-10 left-nf-ds-10 xl:bottom-nf-ds-12 xl:left-nf-ds-16 font-nf-ds-mono text-[10.5px] uppercase tracking-[0.12em] text-nf-ds-white/40">
          nf · New York
        </span>

        {copy.showSignOut && (
          <button
            type="button"
            onClick={handleSignOut}
            className="absolute bottom-nf-ds-10 right-nf-ds-10 xl:bottom-nf-ds-12 xl:right-nf-ds-16 font-nf-ds-mono text-[10.5px] uppercase tracking-[0.12em] text-nf-ds-white/60 hover:text-nf-ds-white transition-colors cursor-pointer"
          >
            Sign out
          </button>
        )}
      </aside>

      <div className="hidden lg:block lg:w-1/2" aria-hidden="true" />

      <main className="flex flex-col w-full lg:w-1/2 min-h-screen bg-nf-ds-cream overflow-auto">
        {/* The wordmark and tagline live in the dark panel, which is hidden
            below lg. Repeat them here on the cream side so the small-viewport
            layout still opens with the brand instead of a bare form. */}
        {copy.showWordmark && (
          <div className="lg:hidden flex items-center gap-nf-ds-4 px-nf-ds-4 pt-nf-ds-8 sm:px-nf-ds-12 sm:pt-nf-ds-10">
            <span className="font-swear text-nf-ds-2xl leading-none tracking-[-0.01em] text-nf-ds-text-ink">
              NewForm
            </span>
            {/* The tagline is ~220px of tracked mono. Inside the page's 48px
                side padding it outruns a phone's width, so below sm the
                wordmark carries the header alone. */}
            {/* --nf-ds-border is tuned as a hairline on white and all but
                vanishes on the cream ground, so the rule is the paper muted
                role at low alpha instead. */}
            <span className="hidden sm:block h-5 w-px bg-nf-ds-fg-muted-on-paper/25" />
            <span className="hidden sm:block font-nf-ds-mono text-[10.5px] uppercase tracking-[0.12em] leading-none text-nf-ds-fg-muted-on-paper">
              {copy.tagline}
            </span>
          </div>
        )}

        {children}
      </main>
    </div>
  );
}
